import { useEffect, useRef, useState } from "react";
import { Car, MapPin, FileText, Plus, Loader2, CheckCircle2, ArrowLeft, Trash2, X } from "lucide-react";
import { fotoApi, fotoError } from "./fotoApi";
import { loadStampPrefs, stampImageFile } from "./fotoStamp";
import { FotoCronologia } from "./FotoCronologia";
import { FotoSourceSheet } from "./FotoSourceSheet";
import { FotoCamera } from "./FotoCamera";

const BTNS = [
  ["veicolo", "Foto Veicolo", Car, "image/*"],
  ["luogo", "Foto Luogo Sinistro", MapPin, "image/*"],
  ["documenti", "Foto Documenti", FileText, "image/*,application/pdf"],
  ["altro", "Altro", Plus, "image/*,application/pdf"],
];

export const FotoUpload = ({ pratica, onBack }) => {
  const inputs = useRef({});
  const [busy, setBusy] = useState(null);
  const [pct, setPct] = useState(0);
  const [done, setDone] = useState([]);
  const [err, setErr] = useState("");
  const [sheet, setSheet] = useState(null);
  const [camera, setCamera] = useState(null);
  const [viewer, setViewer] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [stampPrefs, setStampPrefs] = useState(null);
  const [cronoKey, setCronoKey] = useState(0);
  useEffect(() => { loadStampPrefs().then(setStampPrefs).catch(() => {}); }, []);
  const doneRef = useRef(done);
  doneRef.current = done;
  useEffect(() => () => { doneRef.current.forEach((d) => d.preview && URL.revokeObjectURL(d.preview)); }, []);
  const labelOf = (k) => (BTNS.find((b) => b[0] === k) || [])[1];

  const upload = async (tipo, files, onCount) => {
    setErr("");
    let n = 0;
    for (const raw of Array.from(files || [])) {
      setBusy(tipo); setPct(0);
      try {
        const f = stampPrefs?.foto_on ? await stampImageFile(raw, stampPrefs) : raw;
        const fd = new FormData(); fd.append("file", f); fd.append("tipo", tipo);
        const { data } = await fotoApi.post(`/pratiche/${pratica.id}/upload`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => e.total && setPct(Math.round((e.loaded / e.total) * 100)),
        });
        const preview = (f.type || "").startsWith("image/") ? URL.createObjectURL(f) : null;
        setDone((d) => [{ ...data, preview }, ...d]);
        setCronoKey((k) => k + 1);
        onCount?.(++n);
      } catch (e) { setErr(fotoError(e)); setBusy(null); return false; }
    }
    setBusy(null);
    return true;
  };

  const remove = async (d) => {
    if (!d.id || !window.confirm("Eliminare questa foto?")) return;
    setDeleting(d.id); setErr("");
    try {
      await fotoApi.delete(`/pratiche/${pratica.id}/allegati/${d.id}`);
      if (d.preview) URL.revokeObjectURL(d.preview);
      setDone((list) => list.filter((x) => x.id !== d.id));
      setCronoKey((k) => k + 1);
    } catch (e) { setErr(fotoError(e)); }
    finally { setDeleting(null); }
  };

  return (
    <div className="space-y-4" data-testid="foto-upload">
      <button type="button" onClick={onBack} data-testid="foto-change-pratica" className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 py-2">
        <ArrowLeft className="h-4 w-4" /> Cambia pratica
      </button>
      <div className="rounded-2xl bg-slate-900 text-white p-5" data-testid="foto-pratica-card">
        <div className="text-xs uppercase tracking-wider text-slate-400">Cliente</div>
        <div className="text-xl font-bold break-words" data-testid="foto-pratica-cliente">{pratica.cliente || "—"}</div>
        <div className="mt-3 grid grid-cols-2 gap-3">
          <div><div className="text-xs uppercase tracking-wider text-slate-400">Pratica</div><div className="text-lg font-mono font-semibold break-all" data-testid="foto-pratica-numero">{pratica.numero_pratica || "—"}</div></div>
          <div><div className="text-xs uppercase tracking-wider text-slate-400">Targa</div><div className="text-lg font-mono font-semibold" data-testid="foto-pratica-targa">{pratica.targa || "—"}</div></div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {BTNS.map(([k, label, Icon, accept]) => (
          <div key={k}>
            <button type="button" disabled={!!busy} onClick={() => setSheet(k)} data-testid={`foto-btn-${k}`}
              className="w-full aspect-[4/3] rounded-2xl bg-amber-500 active:bg-amber-600 active:scale-[0.98] transition-transform text-slate-900 flex flex-col items-center justify-center gap-1.5 p-2.5 disabled:opacity-60">
              {busy === k ? <Loader2 className="h-8 w-8 animate-spin" /> : <Icon className="h-8 w-8" />}
              <span className="text-sm font-bold text-center leading-tight">{busy === k ? `${pct}%` : label}</span>
            </button>
            <input ref={(el) => { inputs.current[k] = el; }} type="file" accept={accept} multiple className="hidden"
              onChange={(e) => { const fl = e.target.files; upload(k, fl).finally(() => { e.target.value = ""; }); }} data-testid={`foto-input-${k}`} />
          </div>
        ))}
      </div>
      {err && <p className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700" role="alert" data-testid="foto-upload-error">{err}</p>}
      {sheet && (
        <FotoSourceSheet label={labelOf(sheet)} onClose={() => setSheet(null)}
          onCamera={() => { setCamera(sheet); setSheet(null); }}
          onGallery={() => { inputs.current[sheet]?.click(); setSheet(null); }} />
      )}
      {camera && <FotoCamera label={labelOf(camera)} onClose={() => setCamera(null)} onSend={(files, onCount) => upload(camera, files, onCount)} />}
      {done.length > 0 && (
        <ul className="space-y-2" data-testid="foto-upload-done">
          {done.map((d, i) => (
            <li key={d.id || i} className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2.5" data-testid={`foto-done-${i}`}>
              <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />
              {d.preview ? (
                <button type="button" onClick={() => setViewer(d)} data-testid={`foto-done-thumb-${i}`} className="shrink-0 h-10 w-10 rounded-md overflow-hidden border border-emerald-300 bg-white">
                  <img src={d.preview} alt={d.nome} className="h-full w-full object-cover" />
                </button>
              ) : (
                <span className="shrink-0 h-10 w-10 rounded-md border border-emerald-300 bg-white flex items-center justify-center"><FileText className="h-5 w-5 text-emerald-700" /></span>
              )}
              <span className="flex-1 min-w-0 text-sm text-emerald-900 break-all"><strong>{d.nome}</strong> caricato nella pratica <strong>{d.numero_pratica}</strong> ({d.cartella})</span>
              {d.id && (
                <button type="button" onClick={() => remove(d)} disabled={deleting === d.id} aria-label="Elimina foto" data-testid={`foto-done-delete-${i}`}
                  className="shrink-0 h-10 w-10 rounded-lg flex items-center justify-center text-red-600 active:bg-red-100 disabled:opacity-50">
                  {deleting === d.id ? <Loader2 className="h-5 w-5 animate-spin" /> : <Trash2 className="h-5 w-5" />}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      <FotoCronologia praticaId={pratica.id} refreshKey={cronoKey} />
      {viewer && (
        <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center" onClick={() => setViewer(null)} data-testid="foto-viewer">
          <button type="button" onClick={() => setViewer(null)} aria-label="Chiudi" data-testid="foto-viewer-close" className="absolute top-4 right-4 h-11 w-11 rounded-full bg-white/15 text-white flex items-center justify-center"><X className="h-6 w-6" /></button>
          <img src={viewer.preview} alt={viewer.nome} onClick={(e) => e.stopPropagation()} className="max-h-full max-w-full object-contain" data-testid="foto-viewer-img" />
        </div>
      )}
    </div>
  );
};
